import { useAuth } from "@/context/AuthContext";
import { UserService } from "@/services/UserService";
import { useState, useEffect } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { User } from "@/common/model/User";
import { Input } from "./Input";
import { ENDPOINTS } from "@/constants/api";

interface UserSearchInputProps {
    theme?: string;
    onUserSelect: (user: User) => void;
    adminMode?: boolean;
}

const UserSearchInput: React.FC<UserSearchInputProps> = ({ theme = "dark", onUserSelect, adminMode = false }) => {
    const [query, setQuery] = useState<string>("");
    const [users, setUsers] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { user } = useAuth();

    useEffect(() => {
        let isMounted = true;

        const fetchUsers = async () => {
            if (!query || query.length <= 1) {
                setUsers([]);
                return;
            }
            setIsLoading(true);
            try {
                // In admin mode, if query is a pure number, search by user ID
                if (adminMode && /^\d+$/.test(query.trim())) {
                    const userDetails = await UserService.getUserById(Number(query.trim()), user!.accesstoken);
                    if (isMounted) {
                        if (userDetails) {
                            setUsers([{
                                id: userDetails.userId,
                                name: userDetails.name || "",
                                username: userDetails.username,
                                profilepicid: userDetails.profilePicId,
                                privateaccount: userDetails.privateAccount ?? false,
                            } as User]);
                        } else {
                            setUsers([]);
                        }
                    }
                } else {
                    const results = await UserService.findUsers(query, user!.accesstoken);
                    if (isMounted) {
                        setUsers(results);
                    }
                }
            } catch (error) {
                console.error("Error fetching users:", error);
            }
            setIsLoading(false);
        };

        const timer = setTimeout(fetchUsers, 300);
        return () => {
            isMounted = false;
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);

    return (
        <div className="w-full mb-6">
            <div className="relative w-full">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="text-gray-400 size-5" />
                </div>
                <Input
                    type="text"
                    placeholder={adminMode ? "Search user by name, email or ID" : "Search user by name or email"}
                    className={`w-full pl-10 pr-3 py-2 bg-backgroundbox placeholder-gray-400 focus:outline-none ${
                        theme === "light" ? "text-black" : "text-white"
                    }`}
                    value={query ?? ""}
                    onChange={(e) => setQuery(e.target.value)}
                />
            </div>

            {isLoading && (
                <div className={`mt-3 text-center ${theme === "light" ? "text-black" : "text-white"}`}>Loading...</div>
            )}

            {!isLoading && query && query.length > 1 && users && users.length === 0 && (
                <div className={`mt-3 text-center text-gray-400`}>No users found</div>
            )}

            <div className="mt-3 w-full border border-gray-700">
                {users &&
                    users.length > 0 &&
                    users.map(
                        (item) =>
                            (adminMode || item.id !== user?.id) && (
                                <button
                                    key={item.id}
                                    className="flex items-center w-full p-3 border-b border-gray-700 hover:bg-primary transition duration-200 cursor-pointer"
                                    onClick={() => {
                                        setUsers([]);
                                        setQuery("");
                                        onUserSelect(item);
                                    }}
                                >
                                    <img
                                        src={
                                            item.profilepicid
                                                ? `${ENDPOINTS.IMAGES.PROFILE}/${item.profilepicid}`
                                                : "/default-profilepic.png"
                                        }
                                        alt="Profile"
                                        className="h-10 w-10 rounded-full object-cover"
                                    />
                                    <div className="ml-3 flex flex-col items-start">
                                        <span
                                            className={`text-lg ${theme === "light" ? "text-black" : "text-white"}`}
                                        >
                                            {item.name}
                                        </span>
                                        <span className="text-sm text-gray-400">
                                            {item.username}
                                        </span>
                                    </div>
                                </button>
                            )
                    )}
            </div>
        </div>
    );
};

export { UserSearchInput };
